import { computeObjectiveReadiness } from './objective-readiness-service.js';
import { getCurrentTestVersion } from './assessment-test-service.js';
import { objectivesInProgress } from './program-entry-service.js';
import { objectiveDisplayLabel } from '../programs/objective-label.js';
// ---- labels, groups, document order ----------------------------------------------------
/** The objective's display id (see the header); one leaf module (programs/objective-label.ts). */
export { objectiveDisplayLabel };
/** The criterion's group: SOC 2 series (CC6.1 → CC6), ISO clause (6.1.2 → 6) or Annex A group (A.6.2.4 → A.6). */
export function requirementGroup(program, ref) {
    if (program === 'iso-42001') {
        if (ref.startsWith('A.'))
            return ref.split('.').slice(0, 2).join('.');
        return ref.split('.')[0];
    }
    return ref.split('.')[0];
}
const SOC2_CATEGORY_ORDER = { CC: 0, A: 1, C: 2, PI: 3, P: 4 };
/** A sortable key in document order (numbers compared as numbers). */
export function documentOrderKey(program, ref) {
    const parts = [];
    let rest = ref;
    if (program === 'soc2') {
        const m = /^([A-Z]+)(.*)$/.exec(ref);
        parts.push(SOC2_CATEGORY_ORDER[m?.[1] ?? ''] ?? 9);
        rest = m?.[2] ?? ref;
    }
    else if (program === 'iso-42001') {
        parts.push(ref.startsWith('A.') ? 1 : 0);
        rest = ref.startsWith('A.') ? ref.slice(2) : ref;
    }
    for (const seg of rest.split(/[._-]/)) {
        if (seg === '')
            continue;
        const n = Number(seg.replace(/^obj/, ''));
        parts.push(Number.isFinite(n) && seg !== 'obj' ? n : seg);
    }
    return parts;
}
export function compareDocumentOrder(program, a, b) {
    const ka = documentOrderKey(program, a), kb = documentOrderKey(program, b);
    for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
        const x = ka[i], y = kb[i];
        if (x === undefined)
            return -1;
        if (y === undefined)
            return 1;
        if (x === y)
            continue;
        if (typeof x === 'number' && typeof y === 'number')
            return x - y;
        return String(x) < String(y) ? -1 : 1;
    }
    return 0;
}
// ---- classification + ranking -------------------------------------------------------------
/** One objective's kind (header table). */
export function objectiveKind(o) {
    switch (o.status) {
        case 'met': return 'met';
        case 'not_applicable': return 'na';
        case 'not_met': return 'not';
        case 'no_evidence': return 'noev';
        default: return o.test_keys.length ? 'noev' : 'dec';
    }
}
export const GAP_RANK = { not: 0, noev: 1, dec: 2 };
/** Counts + every open objective in rank order (pure; `rankNextGap` takes the first). */
export function rankGaps(readiness, opts = {}) {
    const program = readiness.program;
    const counts = { total: 0, covered: 0, met: 0, na: 0, not_met: 0, in_progress: 0, no_evidence: 0, to_decide: 0 };
    const reqs = [...readiness.requirements].sort((a, b) => compareDocumentOrder(program, a.ref, b.ref));
    let near = null;
    const cands = [];
    reqs.forEach((r, reqIndex) => {
        const group = requirementGroup(program, r.ref);
        const objectives = [...r.objectives].sort((a, b) => compareDocumentOrder(program, a.ref, b.ref));
        objectives.forEach((o, ordinal) => {
            if (opts.skip && o.objective_id === opts.skip)
                near = { requirement_id: r.requirement_id, group };
            const kind = objectiveKind(o);
            counts.total++;
            if (kind === 'met') {
                counts.met++;
                counts.covered++;
                return;
            }
            if (kind === 'na') {
                counts.na++;
                counts.covered++;
                return;
            }
            const prog = kind === 'not' ? (opts.inProgress?.get(o.objective_id) ?? null) : null;
            if (kind === 'not') {
                counts.not_met++;
                if (prog)
                    counts.in_progress++;
            }
            else if (kind === 'noev')
                counts.no_evidence++;
            else
                counts.to_decide++;
            if (opts.skip && o.objective_id === opts.skip)
                return;
            cands.push({
                tier: prog ? 3 : GAP_RANK[kind],
                reqIndex,
                ordinal,
                gap: {
                    objective_id: o.objective_id, requirement_id: r.requirement_id, requirement_ref: r.ref, requirement_title: r.title, group,
                    label: objectiveDisplayLabel(program, o.ref), statement: o.statement, kind, in_progress: prog,
                    quick_win: kind === 'noev' ? (opts.quickWins?.get(o.objective_id) ?? null) : null,
                },
            });
        });
    });
    const n = near;
    const closeness = (c) => (!n ? 2 : c.gap.requirement_id === n.requirement_id ? 0 : c.gap.group === n.group ? 1 : 2);
    cands.sort((a, b) => a.tier - b.tier || closeness(a) - closeness(b) || a.reqIndex - b.reqIndex || a.ordinal - b.ordinal);
    return { ranked: cands.map((c) => c.gap), counts };
}
/** The single next gap (pure). */
export function rankNextGap(readiness, opts = {}) {
    const { ranked, counts } = rankGaps(readiness, opts);
    return { program: readiness.program, period_id: readiness.period_id, next: ranked[0] ?? null, counts };
}
// ---- quick wins --------------------------------------------------------------------------
/**
 * Evidence-source categories (the test library's vocabulary) a read-only
 * posture connector collects, by connector kind (the same table the CMMC Gaps
 * view uses in the SPA).
 */
export const EVIDENCE_SOURCE_CONNECTOR_KINDS = {
    'Identity provider / directory': ['okta_posture', 'entra_posture'],
    'Cloud control plane': ['aws', 'aws_oidc', 'aws_role', 'gcp'],
    'Vulnerability scanner': ['tenable'],
    'Audit logs / SIEM': ['datadog'],
};
/**
 * For every open (Not met / No evidence) objective: the evidence-source categories of its
 * mapped tests that a read-only connector collects, each with the workspace's active
 * connector of a matching kind (null = not connected yet → "Connect").
 */
async function collectSourcesFor(db, content, workspaceId, readiness) {
    const out = new Map();
    let connectors = [];
    try {
        connectors = (await db.query(`SELECT connector_id, kind FROM evidence_connectors WHERE workspace_id = ? AND status = 'active' ORDER BY connector_id`, [workspaceId])).results;
    }
    catch {
        connectors = [];
    }
    const sourcesOf = new Map();
    for (const r of readiness.requirements) {
        for (const o of r.objectives) {
            const k = objectiveKind(o);
            if (k !== 'noev' && k !== 'not')
                continue;
            const list = [];
            for (const key of o.test_keys) {
                if (!sourcesOf.has(key)) {
                    const v = await getCurrentTestVersion(content, key).catch(() => null);
                    let s = [];
                    try {
                        s = v ? JSON.parse(v.evidence_sources_json || '[]') : [];
                    }
                    catch {
                        s = [];
                    }
                    sourcesOf.set(key, s);
                }
                for (const src of sourcesOf.get(key) ?? []) {
                    const kinds = EVIDENCE_SOURCE_CONNECTOR_KINDS[src];
                    if (!kinds || list.some((x) => x.source === src))
                        continue;
                    const c = connectors.find((x) => kinds.includes(x.kind));
                    list.push({ source: src, connector_id: c?.connector_id ?? null, kind: c?.kind ?? null });
                }
            }
            if (list.length)
                out.set(o.objective_id, list);
        }
    }
    return out;
}
/** The next gap for a period: readiness + live tickets / risk requests + quick wins → the ranker. */
export async function loadNextGap(db, input) {
    const readiness = input.readiness ?? await computeObjectiveReadiness(db, { workspaceId: input.workspaceId, periodId: input.periodId, contentDb: input.contentDb, now: input.now });
    const inProgress = await objectivesInProgress(db, input.workspaceId, input.profileId);
    const collect = await collectSourcesFor(db, input.contentDb ?? db, input.workspaceId, readiness);
    // A quick win: a No evidence point a connected source can collect now.
    const quickWins = new Map();
    for (const [id, list] of collect) {
        const hit = list.find((x) => x.connector_id);
        if (hit)
            quickWins.set(id, { source: hit.source, connector_id: hit.connector_id, kind: hit.kind });
    }
    return {
        ...rankNextGap(readiness, { inProgress, skip: input.skip ?? null, quickWins }), readiness,
        collect: Object.fromEntries(collect), in_progress: Object.fromEntries(inProgress),
    };
}
//# sourceMappingURL=program-next-gap-service.js.map